import { Injectable, isDevMode } from '@angular/core';

const LOCK_NAME = 'marina-auth-credentials';

/**
 * Serialises every operation that can change the auth cookies — login, 2FA completion, refresh,
 * logout — across all tabs of this origin.
 *
 * The race this exists for cannot be fixed on the client side of the response. A refresh sent before
 * the user logged out can land after they have logged in again, and the browser applies its
 * `Set-Cookie` headers when the response arrives, independently of whether the page keeps or discards
 * the body (RFC 6265 §4.1). Discarding a stale result in RxJS therefore protects our own state and
 * nothing else: the cookie jar has already been overwritten, and the new login is broken.
 *
 * So the lock is held from *before the request is sent* until *after the response has arrived*, which
 * is the only window that contains the `Set-Cookie`. A login cannot begin while any tab's refresh is
 * in flight.
 *
 * Holding a lock is not on its own enough — see {@link runExclusive}.
 */
@Injectable({ providedIn: 'root' })
export class AuthCredentialLockService {
  /** Serialises within this tab when Web Locks is unavailable, and orders queueing in every case. */
  private chain: Promise<unknown> = Promise.resolve();

  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.locks?.request;
  }

  /**
   * Run `operation` with the credential lock held.
   *
   * `operation` receives no arguments and must do its own re-check first: Web Locks are FIFO, so by
   * the time a queued operation is granted the lock, the world may have moved on — a logout for
   * session A can be granted *after* a login as B completed, and would then log B out. Acquiring the
   * lock tells you nobody else is writing cookies right now; it tells you nothing about what happened
   * while you waited. Callers therefore re-read shared state as their first act inside.
   *
   * The lock is never re-entrant. An operation that fails must not try to take it again from inside
   * its own callback — schedule that work to run after this one returns.
   */
  async runExclusive<T>(label: string, operation: () => Promise<T>): Promise<T> {
    if (!this.isSupported) {
      // No Web Locks: serialise within this tab at least. Cross-tab ordering is then left to the
      // server's rotation grace window, which tolerates concurrent refreshes by design.
      this.log(`[AuthCredentialLock] Web Locks unavailable; serialising "${label}" in-tab only`);
      return this.queueInTab(operation);
    }

    this.log(`[AuthCredentialLock] Waiting for lock: ${label}`);
    return navigator.locks.request(LOCK_NAME, async () => {
      this.log(`[AuthCredentialLock] Acquired: ${label}`);
      try {
        return await operation();
      } finally {
        this.log(`[AuthCredentialLock] Released: ${label}`);
      }
    }) as Promise<T>;
  }

  private queueInTab<T>(operation: () => Promise<T>): Promise<T> {
    const run = this.chain.then(operation, operation);
    // Keep the chain alive regardless of outcome; a rejected link must not stall every later caller.
    this.chain = run.catch(() => undefined);
    return run;
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
